import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faEmptySet,
  faQuestion,
  faSliders,
} from "@fortawesome/pro-regular-svg-icons";

import {
  isArray,
  isArrayWithItems,
  isObjectWithProperties,
  isTupleArray,
  isUnion,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { TYPE_ICONS } from "src/utils/constants";

export const getTypeIcon = (type: string, label?: string): IconDefinition => {
  // Check for date/datetime formats
  if (type === "string" && label === "date") {
    return TYPE_ICONS.date;
  }
  if (type === "string" && label === "datetime") {
    return TYPE_ICONS.datetime;
  }

  // Check for array types
  if (type.endsWith("[]")) {
    return TYPE_ICONS.array;
  }

  // Check for union types
  if (type === "anyOf" || type === "oneOf" || type === "union") {
    return faSliders;
  }

  // Check for null type
  if (type === "null") {
    return faEmptySet;
  }

  // Use TYPE_ICONS for standard types
  const icon = TYPE_ICONS[type as keyof typeof TYPE_ICONS];
  if (icon) {
    return icon;
  }

  // Default fallback
  return faQuestion;
};

export const generateDataPath = (
  parentPath: string,
  propertyName: string,
  parentSchema?: JsonSchemaNode,
  rootPrefix = "data",
): string => {
  if (!parentPath) {
    return propertyName ? `${rootPrefix}.${propertyName}` : rootPrefix;
  }
  // skip empty names (synthetic or placeholder nodes)
  if (!propertyName) {
    return parentPath;
  }
  if (parentSchema && isArray(parentSchema) && !parentPath.endsWith("[]")) {
    return `${parentPath}[].${propertyName}`;
  }

  return `${parentPath}.${propertyName}`;
};

/**
 * Ellipsizes a data path if it exceeds the specified depth
 * Format: "pathPrefix.firstProperty[...].finalProperty"
 */
export const ellipsizePath = (
  path: string,
  maxDepth: number,
  pathPrefix: string,
): string => {
  if (maxDepth === 0) {
    return path;
  }

  const segments = path.split(".");

  // If path doesn't exceed max depth, return as-is
  if (segments.length <= maxDepth) {
    return path;
  }

  // Find the prefix part and the content part
  const prefixWithDot = pathPrefix + ".";
  const hasPrefix = path.startsWith(prefixWithDot);

  const ellipsisToken = "(...)";

  if (hasPrefix) {
    // Extract segments after the prefix
    const afterPrefixSegments = segments.slice(1); // Remove prefix segment

    if (afterPrefixSegments.length <= maxDepth - 1) {
      return path; // Doesn't exceed depth when accounting for prefix
    }
    const firstContentSegment = afterPrefixSegments[0];
    const lastSegment = afterPrefixSegments[afterPrefixSegments.length - 1];

    return `${pathPrefix}.${firstContentSegment}${ellipsisToken}.${lastSegment}`;
  } else {
    // No prefix
    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];

    return `${firstSegment}${ellipsisToken}.${lastSegment}`;
  }
};

export const asArrayLabel = (itemTypeLabel: string): string => {
  return `${itemTypeLabel}[]`;
};

export const getArrayItemTypeLabel = (arraySchema: JsonSchemaNode): string => {
  const getItemTypeLabel = (arraySchema: JsonSchemaNode) => {
    if (isTupleArray(arraySchema)) {
      return "mixed";
    }
    if (!isArrayWithItems(arraySchema)) {
      return "unknown";
    }

    const itemType = arraySchema.items as JsonSchemaNode;
    if (itemType.$ref) {
      return `${itemType.$ref}`;
    }
    if (isUnion(itemType)) {
      // choice will be resolved elsewhere, but for the parent label we just show union
      const unionType = "anyOf" in itemType ? "anyOf" : "oneOf";
      return `${unionType}`;
    }
    if (isObjectWithProperties(itemType)) {
      return "object";
    }
    if (Array.isArray(itemType.type)) {
      const base =
        itemType.type.filter((t) => t !== "null").join(" | ") || "unknown";
      return `${base}`;
    }
    if (itemType.type) {
      return `${itemType.type}`;
    }
    return "unknown";
  };
  return asArrayLabel(getItemTypeLabel(arraySchema));
};
